import { FlexColumn } from '@fox/layout/src/flex';
import { FormLabel } from '@mui/material';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { roundNumber } from '../../utils/number-functions';
import { ExtendedTextFieldProps, TextField } from '../text-field/index';

const NumberInputLabel = styled(FormLabel)`
	margin-bottom: 0.25rem;
`;

export type NumberUnitLabel = 'px' | '%' | (string & {});

type NumberInputSettings = {
	/**
	 * If true, disable clearing values. If attempted to clear, sets value to 0 if zeroDefault is true, otherwise to min or 0.
	 */
	disableClearable?: boolean;

	/**
	 * Minimum value. This is an html attribute for the input element.
	 */
	min?: number;

	/**
	 * Maximum value. This is an html attribute for the input element.
	 */
	max?: number;

	/**
	 * The increment that the arrow buttons increase and decrease the value by. Also applies to a focused input
	 * when using arrow keys or scroll wheel.
	 */
	step?: number;

	/**
	 * A callback to be fired on on blur of component. This is when we do any number constraining and transformation.
	 */
	onChange?: (val: number | undefined) => void;

	/**
	 * The value of the input.
	 */
	value?: number;

	/**
	 * If true, the input will default to 0 zero when no value is entered
	 * (by default, this controlled component defaults to empty string).
	 */
	zeroDefault?: boolean;

	/**
	 * An optional function to be fired during the onBlur event after internal handleBlur processing.
	 */
	onBlur?: (val: number | undefined) => void;

	/**
	 * Rounds input to specified decimal places
	 */
	fixedDecimals?: number;
};

export type NumberInputProps = NumberInputSettings &
	Omit<ExtendedTextFieldProps, 'value' | 'onChange'> & {
		/**
		 * The unit label appended at the end of the input. While any string will be accepted, for consistency's sake,
		 * an existing label from the `NumberUnitLabel` type should be used. If a suitable label isn't available, it should
		 * be added to the type.
		 */
		unitLabel?: NumberUnitLabel;
	};

function useNumberInput({ disableClearable, min, max, step, value, zeroDefault, onChange, onBlur, fixedDecimals }: NumberInputSettings) {
	const defaultValue = zeroDefault ? 0 : '';
	const [fieldValue, setFieldValue] = React.useState<string | number | undefined>(value ?? defaultValue);
	const [rangeError, setRangeError] = React.useState<string | null>(null);
	const hasMin = typeof min === 'number';
	const hasMax = typeof max === 'number';

	useEffect(() => {
		setFieldValue(value);
	}, [value]);

	const getRangeErrorMessage = (min, max) => {
		if (!hasMin) return `Range: < ${max}`;
		if (!hasMax) return `Range: > ${min}`;
		return `Range: ${min} - ${max}`;
	};

	const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		const value = event.target.value !== '' ? Number(event.target.value) : undefined;
		const isNumber = typeof value === 'number';
		const fixedValue = fixedDecimals !== undefined && isNumber ? roundNumber(value, fixedDecimals) : value;

		if (!hasMin && !hasMax) {
			onChange?.(fixedValue);
			onBlur?.(fixedValue);
			setFieldValue(fixedValue);
			return;
		}

		if (!disableClearable && event.target.value === '') {
			setFieldValue('');
			onChange?.(undefined);
			onBlur?.(undefined);
			setRangeError(null);
			return;
		}

		if (disableClearable && event.target.value === '') {
			const defaultVal = zeroDefault ? 0 : (min ?? 0);
			setFieldValue(defaultVal);
			onChange?.(defaultVal);
			onBlur?.(defaultVal);
			setRangeError(null);
			return;
		}

		if ((hasMin && fixedValue < min) || (hasMax && fixedValue > max)) {
			// Clamp only to the bounds that exist
			let clampedValue = fixedValue;
			if (hasMax && typeof clampedValue === 'number' && clampedValue > max) {
				clampedValue = max;
			}
			if (hasMin && typeof clampedValue === 'number' && clampedValue < min) {
				clampedValue = min;
			}
			setFieldValue(clampedValue);
			onChange?.(clampedValue);
			onBlur?.(clampedValue);
			setRangeError(null);
			return;
		}
		onBlur?.(fixedValue);
		onChange?.(fixedValue);
		setFieldValue(fixedValue);
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value === '' ? defaultValue : event.target.value;

		const valueIsEmpty = value === '';

		let localFieldValue: string | number = value;
		let localRangeError: string | null = null;
		if (valueIsEmpty || isNaN(event.target.valueAsNumber)) {
			localFieldValue = defaultValue;
		} else if ((hasMin && Number(value) < min) || (hasMax && Number(value) > max)) {
			localRangeError = getRangeErrorMessage(min, max);
		}

		setFieldValue(localFieldValue);
		setRangeError(localRangeError);
	};
	const currentValue = fieldValue || fieldValue === 0 ? `${fieldValue}` : defaultValue;
	return {
		onChange: handleChange,
		onBlur: handleBlur,
		value: currentValue,
		InputProps: {
			onBlur: handleBlur,
			inputProps: {
				type: 'number',
				step: step,
				min: min,
				max: max
			}
		},
		rangeError
	};
}

/**
 * An extension of the TextField component for numeric values with some common features built in, including clamping of min/max values, unit suffix, and defaulting to 0.
 */
export const ConsistentNumberInput = ({ label, helperText, ...rest }: NumberInputProps) => {
	return (
		<FlexColumn>
			{label ? <NumberInputLabel>{label}</NumberInputLabel> : null}
			<NumberInput
				// Fallback to empty space to retain horizontal alignment when helperText renders beneath
				helperText={helperText ?? ' '}
				{...rest}
			/>
		</FlexColumn>
	);
};

export const NumberInput = ({
	disableClearable,
	min,
	max,
	step,
	unitLabel,
	value,
	zeroDefault,
	helperText,
	onChange,
	onBlur,
	fixedDecimals,
	variant,
	...rest
}: NumberInputProps) => {
	const { rangeError, ...h } = useNumberInput({
		disableClearable,
		min,
		max,
		step,
		value,
		zeroDefault,
		onChange,
		onBlur,
		fixedDecimals
	});
	return (
		<TextField
			size="small"
			error={!!rangeError || rest.error}
			helperText={rangeError ?? helperText}
			endAdornment={unitLabel}
			variant={variant}
			{...rest}
			{...h}
		/>
	);
};
